import { TransportBase, decoder, encoder, type Transport } from './types.ts';

// A software ELM327 with a car attached to it.
//
// This is not a stub that returns canned strings. It implements the same
// framing the real adapter uses, including the ISO-TP segmentation that makes
// VIN reads awkward, so the protocol layer above it is genuinely exercised.
// If a scan works here and fails on hardware, the bug is in the transport,
// not the parser.

export interface SimVitals {
  rpm: number;
  speedKph: number;
  coolantC: number;
  intakeC: number;
  loadPct: number;
  fuelPct: number;
  voltage: number;
}

export interface SimScenario {
  id: string;
  name: string;
  vin: string;
  protocol: string;
  milOn: boolean;
  storedDtcs: string[];
  pendingDtcs: string[];
  permanentDtcs: string[];
  vitals: SimVitals;
  /**
   * Conditions the ECU froze when the code set. Deliberately unlike the
   * scan-time vitals, because that difference is the entire diagnostic value
   * of a freeze frame.
   */
  freeze: { dtc: string; vitals: SimVitals } | null;
  /** Byte D of PID 01: a set bit means that monitor has not finished. */
  incompleteMonitors: number;
}

export const SCENARIOS: SimScenario[] = [
  {
    id: 'f150-catalyst',
    name: '2013 Ford F-150 — MIL on, catalyst + lean',
    vin: '1FTFW1ET5DFC10312',
    protocol: 'ISO 15765-4 (CAN 11/500)',
    milOn: true,
    storedDtcs: ['P0420', 'P0171'],
    pendingDtcs: ['P0300'],
    permanentDtcs: ['P0420'],
    vitals: { rpm: 782, speedKph: 0, coolantC: 88, intakeC: 31, loadPct: 24, fuelPct: 62, voltage: 14.2 },
    // Set at highway cruise, not at the idle you see in the bay.
    freeze: {
      dtc: 'P0420',
      vitals: { rpm: 2412, speedKph: 71, coolantC: 91, intakeC: 34, loadPct: 61, fuelPct: 64, voltage: 14.1 },
    },
    // Catalyst and EVAP monitors still running after a recent clear.
    incompleteMonitors: 0x05,
  },
  {
    id: 'clean',
    name: '2019 Honda Civic — no faults',
    vin: '19XFC2F59KE201847',
    protocol: 'ISO 15765-4 (CAN 11/500)',
    milOn: false,
    storedDtcs: [],
    pendingDtcs: [],
    permanentDtcs: [],
    vitals: { rpm: 0, speedKph: 0, coolantC: 21, intakeC: 20, loadPct: 0, fuelPct: 84, voltage: 12.4 },
    freeze: null,
    incompleteMonitors: 0x00,
  },
  {
    id: 'bmw-multi',
    name: '2011 BMW 335i — misfire, sensors, network',
    vin: 'WBAKG7C50BE799201',
    protocol: 'ISO 15765-4 (CAN 11/500)',
    milOn: true,
    storedDtcs: ['P0301', 'P0304', 'P0128', 'U0100', 'P0335'],
    pendingDtcs: ['P0302'],
    permanentDtcs: ['P0301'],
    vitals: { rpm: 651, speedKph: 0, coolantC: 76, intakeC: 34, loadPct: 31, fuelPct: 45, voltage: 13.9 },
    // A misfire that set at idle on a cold engine points somewhere very
    // different from one that set under load.
    freeze: {
      dtc: 'P0301',
      vitals: { rpm: 688, speedKph: 0, coolantC: 41, intakeC: 12, loadPct: 22, fuelPct: 47, voltage: 13.6 },
    },
    incompleteMonitors: 0x21,
  },
];

export class SimulatorTransport extends TransportBase implements Transport {
  readonly kind = 'sim' as const;
  readonly label: string;

  private opened = false;
  private buffer = '';
  private scenario: SimScenario;
  /** Set true by mode 04 so the UI can prove clearing worked. */
  private cleared = false;
  private latencyMs: number;

  constructor(scenarioId = SCENARIOS[0].id, latencyMs = 45) {
    super();
    this.scenario = SCENARIOS.find((s) => s.id === scenarioId) ?? SCENARIOS[0];
    this.label = `Simulator — ${this.scenario.name}`;
    this.latencyMs = latencyMs;
  }

  get isOpen() {
    return this.opened;
  }

  async open() {
    this.emitStatus('connecting', 'Starting simulated adapter');
    await delay(120);
    this.opened = true;
    this.cleared = false;
    this.emitStatus('open', this.scenario.name);
  }

  async close() {
    this.opened = false;
    this.emitStatus('closed');
  }

  async write(bytes: Uint8Array) {
    if (!this.opened) throw new Error('Simulator is not open.');
    this.buffer += decoder.decode(bytes);

    // Commands are carriage-return terminated, same as the real thing.
    let idx: number;
    while ((idx = this.buffer.indexOf('\r')) >= 0) {
      const cmd = this.buffer.slice(0, idx).trim();
      this.buffer = this.buffer.slice(idx + 1);
      if (cmd) void this.respond(cmd);
    }
  }

  private async respond(rawCmd: string) {
    const cmd = rawCmd.toUpperCase().replace(/\s+/g, '');
    await delay(this.latencyMs + Math.random() * 25);
    const body = this.answer(cmd);
    // ELM327 terminates every response with CR CR '>'.
    this.emitData(encoder.encode(`${body}\r\r>`));
  }

  private answer(cmd: string): string {
    const s = this.scenario;

    if (cmd.startsWith('AT')) {
      switch (cmd) {
        case 'ATZ':
          return 'ELM327 v1.5';
        case 'ATI':
          return 'ELM327 v1.5';
        case 'ATRV':
          return `${s.vitals.voltage.toFixed(1)}V`;
        case 'ATDP':
          return s.protocol;
        case 'ATDPN':
          return 'A6';
        default:
          return 'OK';
      }
    }

    const stored = this.cleared ? [] : s.storedDtcs;
    const pending = this.cleared ? [] : s.pendingDtcs;
    const milOn = this.cleared ? false : s.milOn;

    switch (cmd) {
      // Supported-PID blocks. Bit 0 of the last byte flags a further block.
      case '0100':
        return can([0x41, 0x00, 0xbe, 0x3f, 0xa8, 0x13]);
      case '0120':
        return can([0x41, 0x20, 0x80, 0x01, 0xa0, 0x01]);
      case '0140':
        return can([0x41, 0x40, 0x44, 0x0c, 0x00, 0x10]);

      case '0101': {
        // A: MIL and code count. B: continuous monitor support, plus the
        // ignition-type bit. C and D: non-continuous support and readiness,
        // where a set bit in D means the test has NOT finished.
        const a = (milOn ? 0x80 : 0x00) | Math.min(stored.length, 0x7f);
        const notReady = this.cleared ? 0x21 : s.incompleteMonitors;
        return can([0x41, 0x01, a, 0x07, 0xe5, notReady]);
      }

      // Mode 06 supported-monitor blocks. Same bitmask shape as mode 01, so
      // the walk continues while the last bit of each block is set.
      case '0600':
        return can([0x46, 0x00, 0x00, 0x00, 0x00, 0x01]);
      case '0620':
        return can([0x46, 0x20, 0x80, 0x00, 0x00, 0x01]);
      case '0640':
        return can([0x46, 0x40, 0x80, 0x00, 0x00, 0x00]);
      case '0621':
        // Catalyst bank 1: measured 0.42 against a 0.60 ceiling.
        return can([0x46, 0x21, 0x80, 0x03, 0x01, 0xa4, 0x00, 0x00, 0x02, 0x58]);
      case '0641':
        // EVAP 0.090 inch leak test, comfortably inside limits.
        return can([0x46, 0x41, 0x8b, 0x0b, 0x02, 0x2f, 0x01, 0x2c, 0x03, 0xe8]);

      // Mode 09: calibration identity.
      case '0904':
        return can([0x49, 0x04, 0x01, ...asciiBytes('PCM-04A7-2211  ')]);
      case '0906':
        return can([0x49, 0x06, 0x01, 0x8d, 0x2a, 0x1f, 0x04]);
      case '090A':
        return can([0x49, 0x0a, 0x01, ...asciiBytes('ECM-EngineControl   ')]);

      case '0902':
        return can([0x49, 0x02, 0x01, ...asciiBytes(s.vin)]);

      case '03':
        return can([0x43, stored.length, ...stored.flatMap(dtcToBytes)]);

      case '07':
        return can([0x47, pending.length, ...pending.flatMap(dtcToBytes)]);

      case '0A':
        return can([0x4a, s.permanentDtcs.length, ...(this.cleared ? [] : s.permanentDtcs.flatMap(dtcToBytes))]);

      case '04':
        // Clearing wipes the freeze frame along with the codes, which is
        // exactly why the UI warns before doing it.
        this.cleared = true;
        return can([0x44]);
    }

    // Mode 01, live data.
    const live = /^01([0-9A-F]{2})$/.exec(cmd);
    if (live) {
      const data = encodePid(parseInt(live[1], 16), s.vitals);
      return data ? can([0x41, parseInt(live[1], 16), ...data]) : 'NO DATA';
    }

    // Mode 02, freeze frame. The request carries a frame number after the PID
    // and the reply echoes it back before the data.
    const freeze = /^02([0-9A-F]{2})([0-9A-F]{2})?$/.exec(cmd);
    if (freeze) {
      const frozen = this.cleared ? null : s.freeze;
      if (!frozen) return 'NO DATA';

      const pid = parseInt(freeze[1], 16);
      const frameNo = freeze[2] ? parseInt(freeze[2], 16) : 0;
      if (frameNo !== 0) return 'NO DATA';

      // PID $02 in this mode reports which code owns the frame.
      if (pid === 0x02) return can([0x42, 0x02, frameNo, ...dtcToBytes(frozen.dtc)]);

      const data = encodePid(pid, frozen.vitals);
      return data ? can([0x42, pid, frameNo, ...data]) : 'NO DATA';
    }

    return 'NO DATA';
  }
}

/**
 * Encode one PID's payload. Shared by live data and freeze frame, because the
 * value encoding is identical in both modes and only the framing differs.
 */
function encodePid(pid: number, v: SimVitals): number[] | null {
  switch (pid) {
    case 0x04:
      return [Math.round((v.loadPct * 255) / 100) & 0xff];
    case 0x05:
      return [(v.coolantC + 40) & 0xff];
    case 0x0c:
      return u16(Math.round(v.rpm * 4));
    case 0x0d:
      return [v.speedKph & 0xff];
    case 0x0f:
      return [(v.intakeC + 40) & 0xff];
    case 0x2f:
      return [Math.round((v.fuelPct * 255) / 100) & 0xff];
    case 0x42:
      return u16(Math.round(v.voltage * 1000));
    // Channels added once PID discovery showed vehicles advertise them.
    case 0x06:
      return [128 + 4]; // short term trim, +3.1%
    case 0x07:
      return [128 + 15]; // long term trim, +11.7% - the lean story
    case 0x0b:
      return [Math.round(30 + v.loadPct * 0.9) & 0xff];
    case 0x0e:
      return [Math.round((12 + 64) * 2) & 0xff]; // 12 degrees advance
    case 0x10:
      return u16(Math.round((v.rpm * v.loadPct) / 90)); // g/s x100
    case 0x11:
      return [Math.round((v.loadPct * 255) / 100) & 0xff];
    case 0x1f:
      return u16(1284);
    case 0x21:
      return u16(137);
    case 0x31:
      return u16(402);
    case 0x33:
      return [99];
    case 0x46:
      return [24 + 40];
    case 0x5c:
      return [(v.coolantC + 6 + 40) & 0xff];
    default:
      return null;
  }
}

// ---------- ELM327 output formatting ----------

/**
 * Render bytes the way an ELM327 prints a CAN response with spaces and headers
 * off. Anything over 7 bytes gets a length line and numbered segment lines,
 * which is exactly the shape that trips up naive VIN parsers.
 */
function can(bytes: number[]): string {
  const hex = (b: number) => b.toString(16).toUpperCase().padStart(2, '0');
  if (bytes.length <= 7) return bytes.map(hex).join('');

  const lines = [bytes.length.toString(16).toUpperCase().padStart(3, '0')];
  // First frame carries 6 bytes; consecutive frames carry 7.
  lines.push('0:' + bytes.slice(0, 6).map(hex).join(''));
  let i = 6;
  let seq = 1;
  while (i < bytes.length) {
    lines.push(`${seq.toString(16).toUpperCase()}:` + bytes.slice(i, i + 7).map(hex).join(''));
    i += 7;
    seq += 1;
  }
  return lines.join('\r');
}

function asciiBytes(s: string): number[] {
  return [...s].map((c) => c.charCodeAt(0));
}

function u16(v: number): number[] {
  return [(v >> 8) & 0xff, v & 0xff];
}

/** "P0420" -> [0x04, 0x20]. Inverse of the decoder in lib/obd/dtc.ts. */
function dtcToBytes(code: string): number[] {
  const kind = { P: 0, C: 1, B: 2, U: 3 }[code[0].toUpperCase()] ?? 0;
  const d1 = parseInt(code[1], 16);
  const rest = parseInt(code.slice(2), 16);
  const a = (kind << 6) | ((d1 & 0x03) << 4) | ((rest >> 8) & 0x0f);
  const b = rest & 0xff;
  return [a, b];
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
